import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Television, Broadcast, Fire, Warning, Trophy, GraduationCap, Star, Confetti,
  CalendarCheck, Megaphone, Medal, MapPin, CaretRight, X,
} from "@phosphor-icons/react";
import { getNewsStates, getNewsColleges, getNewsFeed, getSiteConfig } from "../lib/api";
import { SubscribeButton } from "./SubscribeButton";

const TPL = {
  breaking:    { label: "BREAKING", Icon: Fire,          chip: "bg-rose-600",    glow: "#f43f5e", anim: "nFlip" },
  alert:       { label: "ALERT",    Icon: Warning,       chip: "bg-amber-500",   glow: "#f59e0b", anim: "nSlide" },
  result:      { label: "RESULT",   Icon: Trophy,        chip: "bg-emerald-600", glow: "#10b981", anim: "nZoom" },
  admission:   { label: "ADMISSION",Icon: GraduationCap, chip: "bg-sky-600",     glow: "#0ea5e9", anim: "nRotate" },
  ranking:     { label: "RANKING",  Icon: Star,          chip: "bg-violet-600",  glow: "#8b5cf6", anim: "nPop" },
  event:       { label: "EVENT",    Icon: Confetti,      chip: "bg-pink-600",    glow: "#ec4899", anim: "nFlip" },
  schedule:    { label: "SCHEDULE", Icon: CalendarCheck, chip: "bg-indigo-600",  glow: "#6366f1", anim: "nSlide" },
  notice:      { label: "NOTICE",   Icon: Megaphone,     chip: "bg-teal-600",    glow: "#14b8a6", anim: "nZoom" },
  achievement: { label: "AWARD",    Icon: Medal,         chip: "bg-orange-600",  glow: "#f97316", anim: "nRotate" },
};
const tpl = (t) => TPL[t] || TPL.notice;

// Theme tokens — light is the default; dark keeps the original cinematic look.
const THEMES = {
  dark: {
    bezel: "linear-gradient(180deg,#3f3f46,#0a0a0a 60%)", ring: "ring-black/50",
    screen: "radial-gradient(120% 100% at 50% -10%, #14284d 0%, #0b1020 45%, #060913 100%)",
    scanColor: "#fff", scanOpacity: "0.06", sweep: "bg-white/5",
    blob1: "bg-sky-500/20", blob2: "bg-fuchsia-500/15",
    bar: "bg-black/50 border-white/10", brand: "text-white", brandAccent: "text-sky-400",
    tvIcon: "text-sky-400", clock: "text-emerald-300",
    tabActive: "bg-sky-500 text-white shadow-md shadow-sky-500/40", tabIdle: "bg-white/10 text-zinc-300 hover:bg-white/20",
    guide: "bg-black/30 border-white/10", guideHdr: "text-zinc-500 bg-black/60",
    guideAllActive: "bg-sky-500/20 text-sky-300", guideIdle: "text-zinc-300 hover:bg-white/5",
    guideRowActive: "bg-sky-500/20", guideRowIdle: "hover:bg-white/5",
    guideName: "text-zinc-200", guideNameActive: "text-sky-300", guideSub: "text-zinc-500", guideEmpty: "text-zinc-600",
    card: "bg-white/[0.06] hover:bg-white/[0.12] border-white/10", cardTitle: "text-white group-hover:text-sky-300",
    cardBody: "text-zinc-400", cardMetaName: "text-zinc-400", cardTime: "text-emerald-300",
    emptyIcon: "text-white/50", emptyText: "text-zinc-500", emptyStrong: "text-zinc-300",
    bottom: "border-white/10 bg-black/60", winActive: "bg-rose-500 text-white shadow-md shadow-rose-500/40",
    winIdle: "bg-white/10 text-zinc-300 hover:bg-white/20", chipClear: "bg-sky-500/20 text-sky-300",
    predict: "text-sky-300", tickerWrap: "bg-rose-600", tickerText: "text-white", tickerLabel: "bg-black text-rose-400",
    flash: "bg-black/80 text-white", flashName: "text-amber-400",
  },
  light: {
    bezel: "linear-gradient(180deg,#e4e4e7,#f4f4f5 60%)", ring: "ring-zinc-300/70",
    screen: "radial-gradient(120% 100% at 50% -10%, #eff6ff 0%, #f8fafc 45%, #ffffff 100%)",
    scanColor: "#0f172a", scanOpacity: "0.035", sweep: "bg-sky-400/10",
    blob1: "bg-sky-300/40", blob2: "bg-fuchsia-300/25",
    bar: "bg-white/85 border-zinc-200", brand: "text-[#09090B]", brandAccent: "text-sky-600",
    tvIcon: "text-sky-600", clock: "text-emerald-600",
    tabActive: "bg-sky-500 text-white shadow-md shadow-sky-500/30", tabIdle: "bg-zinc-100 text-zinc-600 hover:bg-zinc-200",
    guide: "bg-zinc-50 border-zinc-200", guideHdr: "text-zinc-400 bg-white/90",
    guideAllActive: "bg-sky-100 text-sky-700", guideIdle: "text-zinc-600 hover:bg-zinc-100",
    guideRowActive: "bg-sky-100", guideRowIdle: "hover:bg-zinc-100",
    guideName: "text-zinc-700", guideNameActive: "text-sky-700", guideSub: "text-zinc-400", guideEmpty: "text-zinc-400",
    card: "bg-white hover:bg-zinc-50 border-zinc-200 shadow-sm", cardTitle: "text-[#09090B] group-hover:text-sky-600",
    cardBody: "text-zinc-500", cardMetaName: "text-zinc-500", cardTime: "text-emerald-600",
    emptyIcon: "text-zinc-300", emptyText: "text-zinc-400", emptyStrong: "text-zinc-600",
    bottom: "border-zinc-200 bg-white/90", winActive: "bg-rose-500 text-white shadow-md shadow-rose-500/30",
    winIdle: "bg-zinc-100 text-zinc-600 hover:bg-zinc-200", chipClear: "bg-sky-100 text-sky-700",
    predict: "text-[#6043EF]", tickerWrap: "bg-rose-500", tickerText: "text-white", tickerLabel: "bg-white text-rose-600",
    flash: "bg-zinc-900/90 text-white", flashName: "text-amber-300",
  },
};

const WINDOWS = [
  { key: "today", label: "LIVE · Today" },
  { key: "week", label: "Past Week" },
  { key: "month", label: "Past Month" },
];

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(1, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const useClock = () => {
  const [t, setT] = useState(new Date());
  useEffect(() => { const id = setInterval(() => setT(new Date()), 1000); return () => clearInterval(id); }, []);
  return t.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
};

export function NewsChannel() {
  const navigate = useNavigate();
  const [states, setStates] = useState([]);
  const [state, setState] = useState(null);
  const [colleges, setColleges] = useState([]);
  const [college, setCollege] = useState(null);
  const [win, setWin] = useState("month");
  const [feed, setFeed] = useState({ items: [], ticker: [] });
  const [featured, setFeatured] = useState(0);
  const [hover, setHover] = useState(false);
  const [theme, setTheme] = useState("light");
  const clock = useClock();
  const listRef = useRef(null);
  const th = THEMES[theme] || THEMES.light;

  useEffect(() => {
    getSiteConfig().then((r) => setTheme(r.data.news_theme === "dark" ? "dark" : "light")).catch(() => {});
  }, []);

  useEffect(() => {
    getNewsStates().then((r) => {
      const s = r.data.states || [];
      setStates(s);
      if (s.length) setState(s[0].state);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (states.length < 2 || hover || college) return;
    const id = setInterval(() => {
      setState((cur) => {
        const idx = states.findIndex((s) => s.state === cur);
        return states[(idx + 1) % states.length].state;
      });
    }, 10000);
    return () => clearInterval(id);
  }, [states, hover, college]);

  useEffect(() => {
    if (!state) return;
    getNewsColleges(state).then((r) => setColleges(r.data.colleges || [])).catch(() => {});
  }, [state]);

  useEffect(() => {
    if (!state) { setFeed({ items: [], ticker: [] }); return; }
    getNewsFeed({ state, college_code: college?.college_code, window: win, limit: 60 })
      .then((r) => { setFeed(r.data); setFeatured(0); }).catch(() => {});
  }, [state, college, win]);

  useEffect(() => {
    const n = Math.min(feed.items.length, 6);
    if (n < 2) return;
    const id = setInterval(() => setFeatured((f) => (f + 1) % n), 5000);
    return () => clearInterval(id);
  }, [feed.items.length]);

  const ticker = useMemo(() => {
    const t = feed.ticker && feed.ticker.length ? feed.ticker : (feed.items || []).map((i) => `${i.college_name}: ${i.title}`);
    return t.length ? t.join("      \u25C6      ") : "No bulletins in this window \u2014 switch to Past Week / Month.";
  }, [feed]);

  if (!states.length) return null;

  const feat = feed.items[featured];

  return (
    <section className="mb-7" data-testid="news-channel" data-theme={theme}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <div className={`relative rounded-[22px] p-2 sm:p-3 shadow-2xl ring-1 ${th.ring}`} style={{ background: th.bezel }}>
        <div className="flex items-center justify-between px-1.5 pb-2">
          <span className={`text-[11px] font-black uppercase tracking-[0.22em] ${th.brand}`}>News &amp; Events</span>
          <SubscribeButton scope="colleges" newsState={state} variant={theme === "dark" ? "dark" : "light"} />
        </div>
        <div className="relative rounded-2xl overflow-hidden" style={{ background: th.screen }}>
          {/* cinematic bg */}
          <div aria-hidden className="absolute inset-0 pointer-events-none">
            <div className={`absolute -top-24 left-[8%] w-72 h-72 rounded-full ${th.blob1} blur-3xl`} style={{ animation: "nBlob 11s ease-in-out infinite" }} />
            <div className={`absolute top-10 right-[10%] w-80 h-80 rounded-full ${th.blob2} blur-3xl`} style={{ animation: "nBlob 14s ease-in-out infinite reverse" }} />
            <div className="absolute inset-0" style={{ opacity: th.scanOpacity, backgroundImage: `repeating-linear-gradient(0deg,${th.scanColor} 0px,${th.scanColor} 1px,transparent 1px,transparent 3px)` }} />
            <div className={`absolute inset-y-0 -left-1/3 w-1/3 skew-x-[-18deg] ${th.sweep}`} style={{ animation: "nSweep 7s linear infinite" }} />
          </div>

          {/* channel bar */}
          <div className={`relative flex items-center gap-2.5 px-3.5 py-3.5 backdrop-blur border-b ${th.bar}`}>
            <Television size={24} weight="fill" className={`${th.tvIcon} shrink-0`} />
            <span className={`font-black tracking-tight ${th.brand} text-base shrink-0`}>NEET<span className={th.brandAccent}>NEWS</span> 24×7</span>
            <span className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-[0.2em] text-rose-500 shrink-0">
              <Broadcast size={13} weight="fill" className="animate-pulse" /> Live
            </span>
            <span className={`font-mono text-[13px] ${th.clock} tabular-nums shrink-0 hidden sm:inline`}>{clock}</span>
            <div className="ml-auto flex gap-1.5 overflow-x-auto no-scrollbar pt-2.5 pb-1 pr-1.5" data-testid="news-state-tabs">
              {states.map((s) => (
                <button key={s.state} data-testid={`news-state-${s.state}`}
                  onClick={() => { setState(s.state); setCollege(null); }}
                  className={`relative shrink-0 px-3 h-7 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${state === s.state ? th.tabActive : th.tabIdle}`}>
                  {s.label || s.state} <span className="opacity-60">{s.count}</span>
                  {s.today > 0 && (
                    <span className="absolute -top-2 -right-1 inline-flex items-center gap-0.5 px-1.5 h-4 rounded-full bg-rose-600 text-white text-[10px] font-black shadow"
                      style={{ animation: "nBadge 1.4s ease-in-out infinite" }}>
                      <span className="w-1 h-1 rounded-full bg-white" />{s.today}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* body */}
          <div className="relative grid grid-cols-1 sm:grid-cols-[190px_1fr]">
            <div className={`hidden sm:flex flex-col border-r max-h-[380px] overflow-y-auto no-scrollbar ${th.guide}`} data-testid="news-college-guide">
              <div className={`px-3 py-2 text-[9px] font-black uppercase tracking-[0.2em] sticky top-0 backdrop-blur ${th.guideHdr}`}>Channel Guide</div>
              <button onClick={() => setCollege(null)}
                className={`text-left px-3 py-1.5 text-[11px] font-semibold border-b border-black/5 ${!college ? th.guideAllActive : th.guideIdle}`}>
                ▸ All of {state}
              </button>
              {colleges.map((c) => (
                <button key={c.college_code} data-testid={`news-college-${c.college_code}`}
                  onClick={() => setCollege(c)}
                  className={`text-left px-3 py-1.5 border-b border-black/5 transition-colors ${college?.college_code === c.college_code ? th.guideRowActive : th.guideRowIdle}`}>
                  <div className={`text-[11px] font-semibold truncate ${college?.college_code === c.college_code ? th.guideNameActive : th.guideName}`}>{c.name}</div>
                  <div className={`flex items-center gap-1 text-[9px] ${th.guideSub}`}>
                    <MapPin size={9} weight="fill" /> {c.city || "—"} · <span className="text-amber-500 font-bold">{c.count}</span>
                  </div>
                </button>
              ))}
              {!colleges.length && <div className={`px-3 py-4 text-[10px] ${th.guideEmpty}`}>No channels.</div>}
            </div>

            <div className="relative min-h-[300px]">
              <div className="absolute left-0 top-0 bottom-8 w-8 sm:w-9 z-20 flex items-center justify-center bg-gradient-to-b from-sky-600 to-indigo-700 border-r border-white/20">
                <span className="text-white text-[9px] font-black uppercase tracking-[0.3em] whitespace-nowrap" style={{ writingMode: "vertical-rl" }}>
                  NEET COUNSEL · YOUR SEAT AWAITS · PREDICT NOW
                </span>
              </div>

              <div ref={listRef} className="pl-10 sm:pl-12 pr-3 py-3 max-h-[300px] overflow-y-auto no-scrollbar space-y-2" data-testid="news-feed-list">
                {feed.items.length === 0 && (
                  <div className={`h-[240px] flex flex-col items-center justify-center text-center ${th.emptyText}`}>
                    <Television size={30} weight="light" className={`mb-2 ${th.emptyIcon}`} />
                    <div className="text-xs">No bulletins for <b className={th.emptyStrong}>{college ? college.name : state}</b> in this window.</div>
                    <div className="text-[10px] mt-1">Try <b>Past Week</b> or <b>Past Month</b> below.</div>
                  </div>
                )}
                {feed.items.map((it, i) => {
                  const T = tpl(it.template);
                  return (
                    <button key={it.id || i} data-testid={`news-item-${i}`}
                      onClick={() => it.college_code ? navigate(`/colleges/${it.college_code}`) : (it.url && window.open(it.url, "_blank", "noopener"))}
                      className={`w-full text-left flex gap-2 items-start rounded-xl border p-2.5 transition-colors group ${th.card}`}
                      style={{ animation: `${T.anim} .5s ease ${Math.min(i, 8) * 0.06}s both` }}>
                      <span className={`shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg ${T.chip} text-white`}
                        style={{ boxShadow: `0 0 14px ${T.glow}66` }}>
                        <T.Icon size={16} weight="fill" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="flex items-center gap-1.5 mb-0.5">
                          <span className={`text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${T.chip} text-white`}>{T.label}</span>
                          <span className={`text-[9px] truncate ${th.cardMetaName}`}>{it.college_name}{it.city ? ` · ${it.city}` : ""}</span>
                          <span className={`ml-auto shrink-0 text-[9px] font-mono ${th.cardTime}`}>{timeAgo(it.published_at)}</span>
                        </span>
                        <span className={`block text-[12px] font-bold leading-snug transition-colors ${th.cardTitle}`}>{it.title}</span>
                        {it.body && <span className={`block text-[10px] leading-snug line-clamp-2 mt-0.5 ${th.cardBody}`}>{it.body}</span>}
                      </span>
                    </button>
                  );
                })}
              </div>

              {feat && (
                <div key={featured} className="absolute left-10 sm:left-12 right-3 bottom-2 z-10 pointer-events-none"
                  style={{ animation: "nLower .6s cubic-bezier(.2,.8,.2,1) both" }}>
                  <div className="flex items-stretch rounded-lg overflow-hidden shadow-xl ring-1 ring-black/40">
                    <span className={`flex items-center px-2 text-[9px] font-black uppercase tracking-widest ${tpl(feat.template).chip} text-white`}>Flash</span>
                    <span className={`flex-1 backdrop-blur px-2.5 py-1.5 text-[11px] font-semibold truncate ${th.flash}`}>
                      <span className={th.flashName}>{feat.college_name}:</span> {feat.title}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* bottom line */}
          <div className={`relative border-t backdrop-blur ${th.bottom}`}>
            <div className="flex items-center gap-1 px-2 py-1.5" data-testid="news-window-tabs">
              {WINDOWS.map((w) => (
                <button key={w.key} data-testid={`news-window-${w.key}`} onClick={() => setWin(w.key)}
                  className={`px-2.5 h-6 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all ${win === w.key ? th.winActive : th.winIdle}`}>
                  {w.label}
                </button>
              ))}
              {college && (
                <button onClick={() => setCollege(null)} data-testid="news-clear-college" className={`ml-1 inline-flex items-center gap-1 px-2 h-6 rounded-full text-[10px] font-bold ${th.chipClear}`}>
                  {college.name.slice(0, 16)} <X size={10} weight="bold" />
                </button>
              )}
              <button onClick={() => navigate("/ug-neet/predict")} className={`ml-auto shrink-0 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider ${th.predict} hover:gap-2 transition-all`}>
                Predict my college <CaretRight size={11} weight="bold" />
              </button>
            </div>
            <div className={`flex items-center h-7 overflow-hidden ${th.tickerWrap}`} data-testid="news-ticker">
              <span className={`z-10 shrink-0 h-full px-2 flex items-center text-[9px] font-black uppercase tracking-[0.2em] ${th.tickerLabel}`}>News Ticker</span>
              <div className="flex-1 overflow-hidden">
                <div className="whitespace-nowrap will-change-transform" style={{ animation: "nTicker 34s linear infinite" }}>
                  <span className={`text-[11px] font-bold px-4 ${th.tickerText}`}>{ticker}</span>
                  <span className={`text-[11px] font-bold px-4 ${th.tickerText}`}>{ticker}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .no-scrollbar::-webkit-scrollbar{display:none}
        .no-scrollbar{-ms-overflow-style:none;scrollbar-width:none}
        @keyframes nBlob{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(14px,-10px) scale(1.08)}}
        @keyframes nSweep{0%{transform:translateX(0) skewX(-18deg)}100%{transform:translateX(600%) skewX(-18deg)}}
        @keyframes nTicker{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
        @keyframes nLower{0%{transform:translateY(120%);opacity:0}100%{transform:translateY(0);opacity:1}}
        @keyframes nFlip{0%{transform:rotateX(-80deg);opacity:0}100%{transform:rotateX(0);opacity:1}}
        @keyframes nSlide{0%{transform:translateX(-24px);opacity:0}100%{transform:translateX(0);opacity:1}}
        @keyframes nZoom{0%{transform:scale(.85);opacity:0}100%{transform:scale(1);opacity:1}}
        @keyframes nRotate{0%{transform:rotateY(75deg);opacity:0}100%{transform:rotateY(0);opacity:1}}
        @keyframes nPop{0%{transform:scale(.6) translateY(10px);opacity:0}60%{transform:scale(1.05)}100%{transform:scale(1) translateY(0);opacity:1}}
        @keyframes nBadge{0%,100%{transform:scale(1)}50%{transform:scale(1.18)}}
      `}</style>
    </section>
  );
}
